import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';

import { Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { areYouSure, confirmRichAction, SwalToast } from 'lib/utils/toast';
import {
  auroMetalSaurus,
  backgroundGrey5,
  positiveGreen,
  squidInk,
  white,
  whiteTransparent70,
} from 'lib/css/colors';
import BrandVerifyCard from 'components/manage_brands/brand_verify/BrandVerifyCard';
import BrandVerifyHeader from 'components/manage_brands/brand_verify/BrandVerifyHeader';
import { BrandVerificationStatus } from 'lib/enums/BrandVerificationStatus';
import { BrandVerificationSuggestionsStatus } from 'lib/enums/BrandVerificationSuggestionsStatus';
import Container from 'components/shared/Container';
import DuplicateBrandsCard from 'components/manage_brands/brand_verify/DuplicateBrandsCard';
import { ManufacturerMode } from 'components/manage_brands/brand_verify/constants';
import MergeBrandsModal from 'components/manage_brands/MergeBrandsModal';
import {
  BRAND_VERIFICATION_SUGGESTIONS_GET_STATS,
  BRANDS_BY_IDS,
  BRANDS_GET_BY_ID,
  BRANDS_UPDATE_MULTI,
  GET_ONE_BRAND_VERIFICATION_SUGGESTION,
  getURL,
  GLOBAL_MANUFACTURERS_GET_BY_ID,
  UPDATE_BRAND_AND_MANUFACTURER_DATA_FROM_BRAND_VERIFICATION_TOOL,
  UPDATE_BRAND_VERIFICATION_SUGGESTION,
  UPDATE_VERIFICATION_STATUS_FROM_SUGGESTION,
} from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import UserPermission from 'lib/enums/UserPermission';
import { isBlank } from 'lib/utils/stringUtils';

const EMPTY_FORM_VALUES = {
  brandId: null,
  eCommName: '',
  brandUrl: '',
  manufacturerMode: ManufacturerMode.NEW,
  manufacturerId: null,
  existingManufacturerName: '',
  existingManufacturerUrl: '',
  existingManufacturerSavedName: '',
  existingManufacturerSavedUrl: '',
  newManufacturerName: '',
  newManufacturerUrl: '',
};

// Lists user-facing labels for fields the reviewer left blank. The backend
// treats blanks as "no change", so we use this list to warn before submitting
// rather than to block.
const getBlankFieldWarnings = formValues => {
  const warnings = [];
  if (isBlank(formValues.brandUrl)) warnings.push('Brand URL');
  if (formValues.manufacturerMode === ManufacturerMode.NEW) {
    if (isBlank(formValues.newManufacturerName)) warnings.push('Manufacturer Name');
    if (isBlank(formValues.newManufacturerUrl)) warnings.push('Manufacturer URL');
  } else if (formValues.manufacturerId != null) {
    if (isBlank(formValues.existingManufacturerName)) warnings.push('Manufacturer Name');
    if (isBlank(formValues.existingManufacturerUrl)) warnings.push('Manufacturer URL');
  }
  return warnings;
};

const renderBlankWarningsHtml = (blanks, leadingLine) => {
  const items = blanks.map(label => `<li>${label}</li>`).join('');
  return `
    <div>${leadingLine}</div>
    <ul style="text-align: left; display: inline-block; margin: 8px auto;">${items}</ul>
    <div>The brand's existing values for these fields will be kept.</div>
  `;
};

const formValuesFromSuggestion = suggestion => {
  const suggestedId = suggestion?.suggested_manufacturer_id || null;
  const suggestedName = suggestion?.suggested_manufacturer_name || '';
  const suggestedUrl = suggestion?.suggested_manufacturer_url || '';
  return {
    brandId: suggestion?.brand?.id ?? null,
    eCommName: suggestion?.ecomm_name || '',
    brandUrl: suggestion?.suggested_brand_url || '',
    manufacturerMode: suggestedId ? ManufacturerMode.EXISTING : ManufacturerMode.NEW,
    manufacturerId: suggestedId,
    existingManufacturerName: suggestedId ? suggestedName : '',
    existingManufacturerUrl: suggestedId ? suggestedUrl : '',
    existingManufacturerSavedName: '',
    existingManufacturerSavedUrl: '',
    newManufacturerName: suggestedId ? '' : suggestedName,
    newManufacturerUrl: suggestedId ? '' : suggestedUrl,
  };
};

function BrandVerifyView() {
  const hasUserPermissions = useUserPermissions();
  const canCleanupBrands = useMemo(
    () => hasUserPermissions([UserPermission.CLEANUP_BRANDS]),
    [hasUserPermissions],
  );
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false);

  const {
    data: suggestion,
    loading,
    refetch,
  } = useGet(GET_ONE_BRAND_VERIFICATION_SUGGESTION, { bookmarked: bookmarkedOnly });
  const { data: stats, refetch: refetchStats } = useGet(BRAND_VERIFICATION_SUGGESTIONS_GET_STATS, {
    bookmarked: bookmarkedOnly,
  });

  const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [mergeContext, setMergeContext] = useState(null);
  const [manualDuplicateBrandIds, setManualDuplicateBrandIds] = useState([]);

  const updateField = useCallback((field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const {
    data: { brand: selectedBrand } = {},
    loading: selectedBrandLoading,
    refetch: refetchSelectedBrand,
  } = useGet(
    selectedBrandId ? getURL(BRANDS_GET_BY_ID, { brand_id: selectedBrandId }) : '',
    {},
    !selectedBrandId,
  );

  const { data: mfrUrlData, loading: mfrUrlLoading } = useGet(
    formValues.manufacturerId
      ? getURL(GLOBAL_MANUFACTURERS_GET_BY_ID, { manufacturer_id: formValues.manufacturerId })
      : '',
    {},
    !formValues.manufacturerId,
  );

  const formValuesReady =
    formValues.brandId != null && formValues.brandId === suggestion?.brand?.id;

  const sortedDuplicateEntries = useMemo(() => {
    const dupes = suggestion?.brand_verification_suggestion_metadata?.possible_duplicate_brands;
    if (!dupes) return [];
    return Object.entries(dupes).sort(([, a], [, b]) => (b?.ce_score ?? 0) - (a?.ce_score ?? 0));
  }, [suggestion]);

  const combinedDuplicateIds = useMemo(() => {
    const autoIds = sortedDuplicateEntries.map(([id]) => Number(id));
    const currentBrandId = suggestion?.brand?.id ?? null;
    const all = [...autoIds, ...manualDuplicateBrandIds.map(Number)];
    const seen = new Set();
    const out = [];
    all.forEach(id => {
      if (id == null || id === currentBrandId) return;
      if (seen.has(id)) return;
      seen.add(id);
      out.push(id);
    });
    return out;
  }, [sortedDuplicateEntries, manualDuplicateBrandIds, suggestion]);

  const idsParam = useMemo(() => combinedDuplicateIds.join(','), [combinedDuplicateIds]);

  const {
    data: brandsResponse,
    loading: duplicateBrandsLoading,
    refetch: refetchDuplicateBrands,
  } = useGet(BRANDS_BY_IDS, { ids: idsParam }, !idsParam);

  const brandsById = useMemo(() => {
    const map = {};
    (brandsResponse?.data ?? []).forEach(brand => {
      map[String(brand.id)] = brand;
    });
    return map;
  }, [brandsResponse]);

  const duplicateBrandsList = useMemo(
    () => combinedDuplicateIds.map(id => brandsById[String(id)]).filter(Boolean),
    [combinedDuplicateIds, brandsById],
  );

  const { postData: postUpdateVerificationStatus, loading: updatingStatus } = usePost(
    UPDATE_VERIFICATION_STATUS_FROM_SUGGESTION,
    null,
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      });
    },
  );

  const { postData: postUpdateSuggestion, loading: skipping } = usePost(
    UPDATE_BRAND_VERIFICATION_SUGGESTION,
    null,
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      });
    },
  );

  const { postData: postUnverifyBrands, loading: unverifying } = usePost(
    BRANDS_UPDATE_MULTI,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Brand(s) unverified successfully',
      });
      refetchDuplicateBrands();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      });
    },
  );

  const { postData: postSaveDuplicateBrand, loading: savingDuplicateBrand } = usePost(
    UPDATE_BRAND_AND_MANUFACTURER_DATA_FROM_BRAND_VERIFICATION_TOOL,
    null,
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      });
    },
  );

  useEffect(() => {
    if (suggestion?.brand) {
      setFormValues(formValuesFromSuggestion(suggestion));
      setSelectedBrandId(null);
      setManualDuplicateBrandIds([]);
    }
  }, [suggestion]);

  useEffect(() => {
    const fetched = mfrUrlData?.manufacturer;
    if (fetched && fetched.id === formValues.manufacturerId) {
      const fetchedName = fetched.name || '';
      const fetchedUrl = fetched.manufacturer_url || '';
      setFormValues(prev => ({
        ...prev,
        existingManufacturerName: fetchedName,
        existingManufacturerUrl: fetchedUrl,
        existingManufacturerSavedName: fetchedName,
        existingManufacturerSavedUrl: fetchedUrl,
      }));
    }
  }, [mfrUrlData, formValues.manufacturerId]);

  const updateVerificationStatus = verificationStatus => {
    const statusLabel =
      verificationStatus === BrandVerificationStatus.VERIFIED ? 'Verified' : 'Unverifiable';
    const isExisting = formValues.manufacturerMode === ManufacturerMode.EXISTING;

    if (isExisting && formValues.manufacturerId == null) {
      SwalToast('info', 'Please pick an existing manufacturer or switch to NEW MANUFACTURER');
      return;
    }

    if (isBlank(formValues.eCommName)) {
      SwalToast('info', 'Brand name is required');
      return;
    }

    const submit = () =>
      postUpdateVerificationStatus({
        brand_id: suggestion.brand.id,
        ecomm_name: formValues.eCommName,
        suggested_brand_url: formValues.brandUrl,
        suggested_manufacturer_id: isExisting ? formValues.manufacturerId : null,
        suggested_manufacturer_name: isExisting
          ? formValues.existingManufacturerName
          : formValues.newManufacturerName,
        suggested_manufacturer_url: isExisting
          ? formValues.existingManufacturerUrl
          : formValues.newManufacturerUrl,
        verification_status: verificationStatus,
      }).then(res => {
        if (res?.response?.data) {
          return;
        }
        SwalToast('success', `Brand marked as ${statusLabel} successfully`);
        refetch();
        refetchStats();
      });

    // Only the VERIFIED branch writes to brand/manufacturer columns, so the
    // soft-blank warning is irrelevant on UNVERIFIABLE.
    const blanks =
      verificationStatus === BrandVerificationStatus.VERIFIED
        ? getBlankFieldWarnings(formValues)
        : [];

    if (blanks.length > 0) {
      const leadingLine =
        `You are about to mark this brand as <strong>${statusLabel}</strong> ` +
        'but the following fields are blank:';
      confirmRichAction(
        submit,
        renderBlankWarningsHtml(blanks, leadingLine),
        'Verify with blank fields?',
        `Yes, mark as ${statusLabel}`,
      );
    } else {
      areYouSure(submit, `Are you sure you want to mark this brand as ${statusLabel}?`);
    }
  };

  const handleActivatePanel = mode => {
    setFormValues(prev =>
      prev.manufacturerMode === mode ? prev : { ...prev, manufacturerMode: mode },
    );
  };

  const handlePickExistingMfr = option => {
    const id = option?.value || null;
    const optionLabel = option?.label || '';
    setFormValues(prev => ({
      ...prev,
      manufacturerMode: ManufacturerMode.EXISTING,
      manufacturerId: id,
      existingManufacturerName: id ? optionLabel : '',
      existingManufacturerUrl: '',
      existingManufacturerSavedName: '',
      existingManufacturerSavedUrl: '',
    }));
  };

  const handleMergeToVerified = () => {
    if (!suggestion?.brand || !selectedBrand) return;
    setMergeContext({ brands: [suggestion.brand, selectedBrand] });
  };

  const handleAfterMergeDuplicates = (mergedChildIds = []) => {
    if (mergedChildIds?.length) {
      const mergedSet = new Set(mergedChildIds.map(Number));
      setManualDuplicateBrandIds(prev => prev.filter(id => !mergedSet.has(Number(id))));
    }
    if (selectedBrandId && mergedChildIds.includes(selectedBrandId)) {
      setSelectedBrandId(null);
      refetchDuplicateBrands();
      return;
    }
    refetchDuplicateBrands();
    if (selectedBrandId) {
      refetchSelectedBrand();
    }
  };

  const handleAddManualDuplicate = brandId => {
    const id = Number(brandId);
    if (!id) return;
    if (suggestion?.brand?.id === id) return;
    setManualDuplicateBrandIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  const handleRemoveManualDuplicate = brandId => {
    const id = Number(brandId);
    setManualDuplicateBrandIds(prev => prev.filter(existing => existing !== id));
    if (selectedBrandId === id) {
      setSelectedBrandId(null);
    }
  };

  const handleUnverifyBrands = ids => {
    if (!ids?.length) return;
    areYouSure(
      () =>
        postUnverifyBrands({
          brand_ids: ids,
          verified: BrandVerificationStatus.UNVERIFIED,
        }),
      `Are you sure you want to unverify ${ids.length} brand(s)?`,
    );
  };

  const handleSaveDuplicateBrandChanges = ({
    brandId,
    name,
    brandUrl,
    manufacturerMode,
    manufacturerId,
    existingManufacturerName,
    existingManufacturerUrl,
    existingManufacturerSavedName,
    existingManufacturerSavedUrl,
    newManufacturerName,
    newManufacturerUrl,
  }) => {
    if (!brandId || !selectedBrand || selectedBrand.id !== brandId) return;

    const isNewMode = manufacturerMode === ManufacturerMode.NEW;
    const isExistingMode = manufacturerMode === ManufacturerMode.EXISTING;

    if (isExistingMode && manufacturerId == null) {
      SwalToast('info', 'Please pick an existing manufacturer or switch to NEW MANUFACTURER');
      return;
    }

    const resolvedManufacturerId = isNewMode ? null : manufacturerId;
    const resolvedManufacturerName = isNewMode ? newManufacturerName : existingManufacturerName;
    const resolvedManufacturerUrl = isNewMode ? newManufacturerUrl : existingManufacturerUrl;

    if (isBlank(name)) {
      SwalToast('info', 'Brand name is required');
      return;
    }

    const originalName = selectedBrand.name ?? '';
    const originalBrandUrl = selectedBrand.brand_url ?? '';
    const originalMfrId = selectedBrand.global_manufacturer?.id ?? null;

    const brandEdited =
      name !== originalName ||
      brandUrl !== originalBrandUrl ||
      resolvedManufacturerId !== originalMfrId;

    const existingManufacturerEdited =
      isExistingMode &&
      (existingManufacturerName !== existingManufacturerSavedName ||
        existingManufacturerUrl !== existingManufacturerSavedUrl);

    const newManufacturerWillBeCreated =
      isNewMode && (!isBlank(resolvedManufacturerName) || !isBlank(resolvedManufacturerUrl));

    if (!brandEdited && !existingManufacturerEdited && !newManufacturerWillBeCreated) {
      SwalToast('info', 'No changes to save');
      return;
    }

    const finishSave = () => {
      SwalToast('success', 'Brand changes saved successfully');
      refetchDuplicateBrands();
      if (selectedBrandId) {
        refetchSelectedBrand();
      }
    };

    const submit = () => {
      postSaveDuplicateBrand({
        brand_id: brandId,
        brand_name: name,
        brand_url: brandUrl,
        manufacturer_id: resolvedManufacturerId,
        manufacturer_name: resolvedManufacturerName,
        manufacturer_url: resolvedManufacturerUrl,
      }).then(res => {
        if (res?.response?.data) return;
        finishSave();
      });
    };

    const blanks = getBlankFieldWarnings({
      brandUrl,
      manufacturerMode,
      manufacturerId,
      existingManufacturerName,
      existingManufacturerUrl,
      newManufacturerName,
      newManufacturerUrl,
    });

    if (blanks.length > 0) {
      confirmRichAction(
        submit,
        renderBlankWarningsHtml(
          blanks,
          'You are about to save changes but the following fields are blank:',
        ),
        'Save with blank fields?',
        'Yes, save changes',
      );
    } else {
      areYouSure(submit, 'Are you sure you want to save these changes?');
    }
  };

  const handleSkip = () => {
    areYouSure(
      () =>
        postUpdateSuggestion({
          suggestion_id: suggestion.id,
          skipped: true,
          display_status: false,
        }).then(res => {
          if (res?.response?.data) {
            return;
          }
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Brand skipped successfully',
          });
          refetch();
          refetchStats();
        }),
      'Are you sure you want to skip this brand?',
    );
  };

  const handleBookmark = () => {
    areYouSure(
      () =>
        postUpdateSuggestion({
          suggestion_id: suggestion.id,
          bookmarked: true,
          display_status: false,
        }).then(res => {
          if (res?.response?.data) {
            return;
          }
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Brand bookmarked successfully',
          });
          refetch();
          refetchStats();
        }),
      'Bookmark this brand for later review?',
    );
  };

  return (
    <Container className={css(styles.pageContainer)}>
      <BrandVerifyHeader
        stats={stats}
        canCleanupBrands={canCleanupBrands}
        bookmarkedOnly={bookmarkedOnly}
        onBookmarkedOnlyChange={() => setBookmarkedOnly(prev => !prev)}
      />
      {(loading && !suggestion?.brand) || (suggestion?.brand && !formValuesReady) ? (
        <div className={classNames(css(styles.emptyState), 'text-center p-5')}>
          <Spinner animation="border" variant="success" className="mb-3" />
          <h5 className={css(styles.emptyTitle)}>Loading next brand</h5>
        </div>
      ) : !suggestion?.brand ? (
        <div className={classNames(css(styles.emptyState), 'text-center p-5')}>
          <FontAwesomeIcon icon={faCheckCircle} className={css(styles.emptyIcon)} />
          <h5 className={css(styles.emptyTitle)}>All caught up</h5>
          <p className={css(styles.emptyBody)}>You have reviewed every brand in this batch.</p>
        </div>
      ) : (
        <div className={css(styles.splitLayout)}>
          <div className={css(styles.splitColumn, styles.relativeColumn)}>
            <BrandVerifyCard
              key={suggestion.brand.id}
              suggestion={suggestion}
              formValues={formValues}
              updateField={updateField}
              onPickExistingMfr={handlePickExistingMfr}
              onActivatePanel={handleActivatePanel}
              manufacturerUrlLoading={mfrUrlLoading}
              updateVerificationStatus={updateVerificationStatus}
              onMergeToVerified={handleMergeToVerified}
              onSkip={handleSkip}
              onBookmark={handleBookmark}
              updatingStatus={updatingStatus || skipping}
            />
            {(updatingStatus || skipping || unverifying || savingDuplicateBrand) && (
              <div className={css(styles.cardOverlay)}>
                <Spinner animation="border" variant="success" />
                <span className={css(styles.cardOverlayText)}>
                  {skipping
                    ? 'Skipping brand'
                    : unverifying
                    ? 'Unverifying brands'
                    : savingDuplicateBrand
                    ? 'Saving brand changes'
                    : 'Updating verification status'}
                </span>
              </div>
            )}
          </div>
          <div className={css(styles.splitColumn)}>
            <DuplicateBrandsCard
              sortedDuplicateEntries={sortedDuplicateEntries}
              duplicateBrandsList={duplicateBrandsList}
              brandsById={brandsById}
              brandsLoading={duplicateBrandsLoading}
              brandsInitialLoading={duplicateBrandsLoading && !brandsResponse}
              currentBrandId={suggestion?.brand?.id}
              selectedBrandId={selectedBrandId}
              onSelectBrandId={setSelectedBrandId}
              selectedBrand={selectedBrand}
              selectedBrandLoading={selectedBrandLoading}
              onSaveChanges={handleSaveDuplicateBrandChanges}
              onAfterMergeDuplicates={handleAfterMergeDuplicates}
              onUnverifyBrands={handleUnverifyBrands}
              saving={savingDuplicateBrand}
              unverifying={unverifying}
              updatingStatus={updatingStatus}
              skipping={skipping}
              manualDuplicateBrandIds={manualDuplicateBrandIds}
              onAddManualDuplicate={handleAddManualDuplicate}
              onRemoveManualDuplicate={handleRemoveManualDuplicate}
            />
          </div>
        </div>
      )}
      {mergeContext ? (
        <MergeBrandsModal
          mergeList={mergeContext.brands}
          setMergeList={brands => setMergeContext({ brands })}
          onHide={() => setMergeContext(null)}
          onMerge={() => {
            postUpdateSuggestion({
              suggestion_id: suggestion.id,
              status: BrandVerificationSuggestionsStatus.MERGED,
            }).then(res => {
              if (res?.response?.data) return;
              refetch();
              refetchDuplicateBrands();
              refetchStats();
            });
          }}
        />
      ) : null}
    </Container>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    backgroundColor: backgroundGrey5,
    minHeight: 'calc(100vh - 60px)',
  },
  splitLayout: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  splitColumn: {
    flex: '1 1 0',
    minWidth: 0,
  },
  relativeColumn: {
    position: 'relative',
  },
  cardOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: whiteTransparent70,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    zIndex: 10,
  },
  cardOverlayText: {
    color: squidInk,
    fontWeight: 600,
  },
  emptyState: {
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${backgroundGrey5}`,
  },
  emptyIcon: {
    color: positiveGreen,
    fontSize: '2.5rem',
    marginBottom: 12,
  },
  emptyTitle: {
    fontWeight: 700,
    color: squidInk,
  },
  emptyBody: {
    color: auroMetalSaurus,
    marginBottom: 16,
  },
});

export default BrandVerifyView;
